/**
 * Oracle Object Context Menu Service
 *
 * Context menu actions for Oracle-specific database objects:
 * packages, sequences, synonyms, DB links, materialized views, types.
 */

import { ContextMenuAction } from '../../explorer/models/TreeModel';
import { DatabaseObjectTreeNode, DatabaseObjectNodeMetadata } from '../../explorer/models/DatabaseTreeModels';
import { toast } from '../../../components/ui/toaster';
import { writeClipboardText } from '../../../lib/clipboard';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { findExistingEditorTab } from '../../workspace/pl-sql-debugger';
import { createDefaultPlSqlEditorContent } from '../../workspace/plsql-editor/types';
import { parseNodeId } from '../../explorer/utils/nodeIdUtils';
import { openObjectScriptTab } from './objectScriptTab';
import type { DatabaseObjectType } from '../models/databaseObjectTypes';
import type { OracleDetailObjectKind } from '../../workspace/core/types/workspaceTypes';
import { isBetaFeatureEnabled } from '../../../core/beta';
import { safeConnectionAlias, safeConnectionServer } from '../../../types/safeConnectionDisplay';

/** Object types that support the "View Details" context menu action */
const DETAIL_OBJECT_TYPES: DatabaseObjectType[] = ['sequence', 'synonym', 'db-link', 'materialized-view', 'type'];

const OBJECT_LABELS: Record<string, string> = {
  'package': 'package',
  'sequence': 'sequence',
  'synonym': 'synonym',
  'db-link': 'DB link',
  'materialized-view': 'materialized view',
  'type': 'type',
  'property-graph': 'property graph',
};

export function generateOracleObjectContextMenuActions(
  objectType: DatabaseObjectType,
): ContextMenuAction[] {
  const label = OBJECT_LABELS[objectType] || objectType;

  const actions: ContextMenuAction[] = [
    { id: 'copy-name', label: `Copy ${label} name`, icon: 'clipboard' },
  ];

  // Package-specific actions
  if (objectType === 'package') {
    actions.push({ id: 'open-in-editor', label: 'Open', icon: 'code' });
    if (isBetaFeatureEnabled('pl-sql-debugger')) {
      actions.push({ id: 'compile-for-debug', label: 'Compile for Debug', icon: 'bug-play' });
    }
  }

  // View Details for non-package object types
  if (DETAIL_OBJECT_TYPES.includes(objectType)) {
    actions.push({ id: 'view-details', label: `View ${label} details`, icon: 'info' });
  }

  // Sequence-specific: Get Next Value
  if (objectType === 'sequence') {
    actions.push({ id: 'get-next-value', label: 'Get Next Value', icon: 'play' });
  }

  // Materialized view-specific: Refresh
  if (objectType === 'materialized-view') {
    actions.push({ id: 'refresh-mview', label: 'Refresh Materialized View', icon: 'database' });
  }

  // DB Link-specific: Test Connection
  if (objectType === 'db-link') {
    actions.push({ id: 'test-dblink', label: 'Test Connection', icon: 'link' });
  }

  // Property graph-specific: GRAPH_TABLE query template
  if (objectType === 'property-graph') {
    actions.push({ id: 'query-graph-table', label: 'Query with GRAPH_TABLE', icon: 'play' });
  }

  actions.push(
    { id: 'script-create', label: 'Script as CREATE', icon: 'file-plus' },
    { id: 'script-drop', label: 'Script as DROP', icon: 'trash' },
    { id: 'separator', label: '', isSeparator: true },
    { id: 'refresh', label: 'Refresh', icon: 'refresh' },
  );

  return actions;
}

function extractDatabaseFromParentId(parentId: string | null | undefined): string | undefined {
  if (!parentId) return undefined;
  const parts = parseNodeId(parentId);
  if (parts.length >= 3) return parts[2];
  return undefined;
}

export function executeOracleObjectContextMenuAction(
  actionId: string,
  nodeId: string,
  handlers: { onRefresh?: (nodeId: string) => void } = {},
  node?: DatabaseObjectTreeNode,
): void {
  if (actionId === 'refresh') {
    handlers.onRefresh?.(nodeId);
    return;
  }

  if (!node) {
    toast.error({ title: 'Error', description: 'Node not found' });
    return;
  }

  const metadata = node.metadata as DatabaseObjectNodeMetadata;
  const { objectDetails } = metadata;
  const { name, schema, connectionId, type: objectType } = objectDetails;

  if (actionId === 'copy-name') {
    void writeClipboardText(name);
    toast.success({ title: 'Copied', description: `${name} copied to clipboard` });
    return;
  }

  const databaseFromParent = extractDatabaseFromParentId(node.parentId);
  const databaseFromMetadata = objectDetails.metadata?.database as string | undefined;
  const { activeConnection, connection, engine } = getConnectionById(connectionId);
  const database = databaseFromParent || databaseFromMetadata || activeConnection?.connection.database || '';

  // View Details: open oracle-object-detail tab
  if (actionId === 'view-details' && DETAIL_OBJECT_TYPES.includes(objectType as DatabaseObjectType)) {
    const { addTab } = useWorkspaceStore.getState();
    addTab({
      title: `${schema}.${name}`,
      type: 'oracle-object-detail',
      content: {
        objectName: name,
        schema,
        database,
        objectKind: objectType as OracleDetailObjectKind,
        isLoading: true,
      },
      connectionInfo: {
        connectionId,
        connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
        serverHost: safeConnectionServer(activeConnection?.connection),
        database,
      },
    });
    return;
  }

  // Quick actions that execute directly (no tab needed)
  if (actionId === 'get-next-value' || actionId === 'refresh-mview' || actionId === 'test-dblink') {
    void (async () => {
      try {
        const result = await window.electronAPI!.oracleObjects.executeAction({
          connectionId,
          schema,
          objectName: name,
          objectKind: objectType as OracleDetailObjectKind,
          action: actionId,
        });
        if (result.success && result.message) {
          toast.success({ title: 'Success', description: result.message });
        }
        if (!result.success && result.error) {
          toast.error({ title: 'Error', description: result.error });
        }
      } catch (error) {
        toast.error({ title: 'Error', description: error instanceof Error ? error.message : 'Action failed' });
      }
    })();
    return;
  }

  if (actionId === 'open-in-editor' && objectType === 'package') {
    const { addTab, tabs, setActiveTab } = useWorkspaceStore.getState();
    const existingId = findExistingEditorTab(tabs, schema, name, connectionId);
    if (existingId) { setActiveTab(existingId); return; }
    addTab({
      title: `${schema}.${name}`,
      type: 'plsql-editor',
      content: createDefaultPlSqlEditorContent(name, schema, database, 'package'),
      connectionInfo: {
        connectionId,
        connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
        serverHost: safeConnectionServer(activeConnection?.connection),
        database,
      },
    });
    return;
  }

  if (actionId === 'compile-for-debug' && objectType === 'package') {
    void (async () => {
      try {
        const result = await window.electronAPI!.oracleDebugger.compileForDebug({
          connectionId,
          schema,
          objectName: name,
          objectType: 'package',
        });
        if (result.success) {
          toast.success({ title: 'Compiled with debug info' });
        } else {
          toast.error({ title: 'Compilation failed', description: result.error ?? 'Unknown error' });
        }
      } catch (error) {
        toast.error({ title: 'Error', description: error instanceof Error ? error.message : 'Compilation failed' });
      }
    })();
    return;
  }

  if (actionId === 'query-graph-table' && objectType === 'property-graph') {
    window.electronAPI?.analytics?.track('feature:property_graph_query_template', { engine }).catch(() => {});
    const tabId = openObjectScriptTab(
      connectionId,
      database,
      name,
      `-- Building GRAPH_TABLE query for ${schema}.${name}...\n`,
      'GRAPH_TABLE',
      true,
    );

    void (async () => {
      const { updateTabContent } = useWorkspaceStore.getState();
      try {
        const { buildGraphTableTemplate, fetchGraphTemplateMetadata, fetchPgsqlGraphTemplateMetadata } = await import('./propertyGraphQueryTemplate');
        const meta = engine === 'pgsql'
          ? await fetchPgsqlGraphTemplateMetadata(connectionId, database, schema, name)
          : await fetchGraphTemplateMetadata(connectionId, database, schema, name);
        const script = buildGraphTableTemplate({
          schema,
          graphName: name,
          vertexLabel: meta.vertexLabel,
          edgeLabel: meta.edgeLabel,
          vertexProperties: meta.vertexProperties,
        });
        updateTabContent(tabId, { query: script, isGeneratingScript: false });
      } catch (error) {
        updateTabContent(tabId, {
          query: `-- Error: ${error instanceof Error ? error.message : 'Unknown error'}\n`,
          isGeneratingScript: false,
        });
      }
    })();
    return;
  }

  if (actionId === 'script-create' || actionId === 'script-drop') {
    const mode = actionId === 'script-create' ? 'create' : 'drop';
    const tabId = openObjectScriptTab(
      connectionId,
      database,
      name,
      `-- Loading ${mode.toUpperCase()} script for ${schema}.${name}...\n`,
      mode.toUpperCase(),
      true,
    );

    void (async () => {
      try {
        const { fetchOracleObjectScript, fetchPgsqlPropertyGraphScript } = await import('./objectDefinitionService');
        const { script, error } = engine === 'pgsql' && objectType === 'property-graph'
          ? await fetchPgsqlPropertyGraphScript({ connectionId, database, schema, name, mode })
          : await fetchOracleObjectScript({
              connectionId,
              database,
              schema,
              name,
              objectType: objectType as 'package' | 'sequence' | 'synonym' | 'materialized-view' | 'type' | 'db-link' | 'property-graph',
              mode,
            });

        const { updateTabContent } = useWorkspaceStore.getState();
        updateTabContent(tabId, { query: script, isGeneratingScript: false });

        if (error) {
          toast.error({ title: 'Script Error', description: error });
        }
      } catch (error) {
        const { updateTabContent } = useWorkspaceStore.getState();
        updateTabContent(tabId, {
          query: `-- Error: ${error instanceof Error ? error.message : 'Unknown error'}\n`,
          isGeneratingScript: false,
        });
      }
    })();
  }
}
